/**
 * Requests over time, drawn with uPlot, with a hover readout.
 *
 * It replaced a hand drawn SVG line that could not tell anybody a number: a
 * person looking at a spike on Thursday had to guess whether it was forty
 * requests or four thousand. uPlot was picked over the React chart libraries
 * because it is small (about 20 KB gzipped), draws to one canvas, and has no
 * animation of its own, which matters on a screen that spends its motion
 * budget on nothing.
 *
 * WHAT THE HOVER SHOWS. A vertical hairline snaps to the nearest bucket and
 * one readout lists every series at that bucket, values first. The readout is
 * built with textContent only, never innerHTML. The last bucket is the one
 * `now` is in, so it is still filling up, and the readout says "so far"
 * rather than letting a half day read as a drop.
 *
 * COLOUR COMES FROM THE TOKENS. A canvas cannot read a CSS custom property,
 * so each token is resolved through a probe element's computed colour and the
 * chart is rebuilt when the theme attribute flips. Requests are ink, because
 * they are a description. Errors wear --state-failed, because there the data
 * is a state. Both series share one axis: an error is a request, so they are
 * the same unit and a second scale would only invite a misread.
 */
import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

import { count } from '../../lib/format';
import '../../styles/components/requests-chart.css';

export interface RequestsChartPoint {
  /** Start of the bucket, ISO. */
  at: string;
  requests: number;
  errors: number;
}

export interface RequestsChartMark {
  at: string;
  version: string;
  failed: boolean;
}

export interface RequestsChartProps {
  points: RequestsChartPoint[];
  /** Deploys inside the window. Each is a line tagged with its version. */
  marks: RequestsChartMark[];
  bucket: 'hour' | 'day';
  /** Draw the errors series and list it in the readout. */
  showErrors: boolean;
  /** BCP 47 locale for every date and time the chart prints. */
  locale: string;
  label: string;
  /** Called once per chart build, the first time somebody reads a value. */
  onFirstHover?: (input: 'mouse' | 'touch') => void;
  /** Called once per chart build, the first time a deploy is highlighted. */
  onFirstDeployFocus?: () => void;
  words: {
    requests: string;
    errors: string;
    soFar: string;
    deploy: (version: string, time: string) => string;
    deployFailed: (version: string, time: string) => string;
  };
}

interface Palette {
  ink: string;
  inkFill: string;
  quiet: string;
  grid: string;
  error: string;
  deploy: string;
  surface: string;
}

/** Resolve a token to a colour a canvas accepts, via the browser's parser. */
function resolve(host: HTMLElement, token: string): string {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.style.display = 'none';
  host.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  return value;
}

/**
 * The same colour at a given opacity, as eight digit hex. The numbers are
 * pulled out bare (see wave-palette.ts for why a function-shaped pattern is
 * avoided in this codebase), and a 0..1 component form is handled as well as
 * the 0..255 one browsers return for hex tokens today.
 */
function withAlpha(color: string, alpha: number): string {
  const nums = (color.match(/[\d.]+/g) ?? []).slice(0, 3).map(Number);
  if (nums.length < 3 || nums.some((n) => !Number.isFinite(n))) return color;
  const scale = nums.every((n) => n <= 1) ? 255 : 1;
  const hex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${nums.map((n) => hex(n * scale)).join('')}${hex(alpha * 255)}`;
}

function palette(host: HTMLElement): Palette {
  const ink = resolve(host, '--text-1');
  return {
    ink,
    inkFill: withAlpha(ink, 0.08),
    quiet: resolve(host, '--text-3'),
    grid: withAlpha(resolve(host, '--border-quiet'), 0.5),
    error: resolve(host, '--state-failed'),
    deploy: resolve(host, '--accent'),
    surface: resolve(host, '--bg-surface'),
  };
}

/** Whole numbers only on the axis: half a request is not a thing. */
const INCRS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1_000, 2_000, 5_000, 10_000, 20_000, 50_000,
  100_000, 200_000, 500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000];

function compact(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

export function RequestsChart({
  points,
  marks,
  bucket,
  showErrors,
  locale,
  label,
  onFirstHover,
  onFirstDeployFocus,
  words,
}: RequestsChartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  // Callers rebuild these arrays and the words object on every render. The
  // chart is rebuilt only when their CONTENT changes, so a parent re-render
  // does not tear down the canvas under a person's pointer.
  const dataKey = JSON.stringify([points, marks]);
  const wordsKey = JSON.stringify([words.requests, words.errors, words.soFar, words.deploy('', ''), words.deployFailed('', '')]);
  const latest = useRef({ points, marks, words });
  latest.current = { points, marks, words };
  const hoverRef = useRef(onFirstHover);
  hoverRef.current = onFirstHover;
  const deployFocusRef = useRef(onFirstDeployFocus);
  deployFocusRef.current = onFirstDeployFocus;

  useEffect(() => {
    const host = hostRef.current;
    const target = plotRef.current;
    const tip = tipRef.current;
    if (!host || !target || !tip) return;
    const { points, marks, words } = latest.current;

    const xs = points.map((p) => Date.parse(p.at) / 1000);
    const reqs = points.map((p) => p.requests);
    const errs = points.map((p) => p.errors);
    const deploys = marks
      .map((m) => ({ sec: Date.parse(m.at) / 1000, version: m.version, failed: m.failed }))
      .filter((m) => Number.isFinite(m.sec));
    const markSecs = deploys.map((m) => m.sec);
    const stepSec = xs.length > 1 ? xs[1] - xs[0] : bucket === 'hour' ? 3600 : 86400;
    const lastIndex = xs.length - 1;

    const dayFmt = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
    const timeFmt = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const tickDay = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

    function bucketTitle(index: number): string {
      const start = new Date(xs[index] * 1000);
      if (bucket === 'day') return dayFmt.format(start);
      return `${dayFmt.format(start)}, ${timeFmt.format(start)}`;
    }

    let plot: uPlot | null = null;
    let hovered = false;
    let deployFocused = false;
    // The deploy nearest the pointer, when one is close enough to mean it.
    let active: number | null = null;
    // True while a finger is holding the chart, so the readout follows it.
    let touching = false;
    let lastInput: 'mouse' | 'touch' = 'mouse';

    /** How close, in CSS pixels, the pointer must be to a deploy line. */
    const nearPx = () => (lastInput === 'touch' ? 24 : 12);

    function nearestDeploy(u: uPlot, left: number): number | null {
      let best: number | null = null;
      let bestDist = Infinity;
      deploys.forEach((d, i) => {
        const dist = Math.abs(u.valToPos(d.sec, 'x') - left);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return bestDist <= nearPx() ? best : null;
    }

    function build() {
      plot?.destroy();
      const colors = palette(host!);
      const width = Math.max(200, target!.clientWidth);

      // The last bucket is still filling up, so the segment into it is drawn
      // dashed as its own series. A solid line there reads as a drop.
      const settled = reqs.map((v, i) => (i === lastIndex && lastIndex > 0 ? null : v));
      const partial = reqs.map((v, i) => (i >= lastIndex - 1 ? v : null));
      const dots = (_u: uPlot, _s: number, i0: number, i1: number) => i1 - i0 < 40;
      const series: uPlot.Series[] = [
        {},
        {
          label: words.requests,
          stroke: colors.ink,
          width: 1.5,
          fill: colors.inkFill,
          points: { show: dots, size: 5, fill: colors.ink },
        },
        {
          label: words.soFar,
          stroke: colors.ink,
          width: 1.5,
          dash: [4, 4],
          fill: colors.inkFill,
          points: { show: dots, size: 5, fill: colors.ink },
        },
      ];
      const data: uPlot.AlignedData = [xs, settled, partial];
      if (showErrors) {
        series.push({
          label: words.errors,
          stroke: colors.error,
          width: 1.5,
          points: { show: false },
        });
        data.push(errs);
      }

      const opts: uPlot.Options = {
        width,
        height: target!.clientHeight || 200,
        legend: { show: false },
        // Room above the plot for the version tags.
        padding: [deploys.length > 0 ? 26 : 12, 28, 0, 0],
        cursor: {
          y: false,
          points: { size: 8, width: 2 },
          drag: { x: false, y: false, setScale: false },
        },
        scales: {
          // Stretched past the last bucket's start only as far as a deploy
          // inside it, so a deploy made today is not dropped off the edge.
          x: {
            time: true,
            auto: false,
            min: xs[0],
            // A little past the latest deploy, so its line is not glued to
            // the right edge, and never more than one bucket past the data.
            max: Math.min(
              xs[lastIndex] + stepSec,
              Math.max(xs[lastIndex], ...markSecs.map((m) => m + (xs[lastIndex] - xs[0]) * 0.02)),
            ),
          },
          y: { range: (_u, _min, max) => [0, max <= 0 ? 4 : Math.ceil(max * 1.12)] },
        },
        axes: [
          {
            stroke: colors.quiet,
            grid: { show: false },
            ticks: { stroke: colors.grid, width: 1, size: 4 },
            font: '11px ui-monospace, SFMono-Regular, Menlo, monospace',
            space: 64,
            // Whole buckets only, or a daily chart labels one day twice.
            incrs:
              bucket === 'hour'
                ? [3600, 2 * 3600, 3 * 3600, 6 * 3600, 12 * 3600, 86400]
                : [86400, 2 * 86400, 7 * 86400, 14 * 86400],
            values: (_u, splits) =>
              splits.map((s) => {
                const at = new Date(s * 1000);
                return bucket === 'hour' ? timeFmt.format(at) : tickDay.format(at);
              }),
          },
          {
            stroke: colors.quiet,
            grid: { stroke: colors.grid, width: 1 },
            ticks: { show: false },
            font: '11px ui-monospace, SFMono-Regular, Menlo, monospace',
            incrs: INCRS,
            size: 44,
            values: (_u, splits) => splits.map((s) => compact(s, locale)),
          },
        ],
        series,
        hooks: {
          draw: [
            (u) => {
              if (deploys.length === 0) return;
              const ctx = u.ctx;
              const px = uPlot.pxRatio;
              const { top, height, left, width } = u.bbox;
              ctx.save();
              ctx.font = `${10 * px}px ui-monospace, SFMono-Regular, Menlo, monospace`;
              ctx.textBaseline = 'middle';
              ctx.textAlign = 'left';

              const padX = 4 * px;
              const tagH = 14 * px;
              const tagY = top - tagH - 4 * px;
              const place = (i: number) => {
                const d = deploys[i];
                const x = Math.round(u.valToPos(d.sec, 'x', true)) + 0.5;
                const tagW = d.version ? ctx.measureText(d.version).width + padX * 2 : 0;
                const tagX = Math.max(left, Math.min(left + width - tagW, x - tagW / 2));
                return { d, x, tagW, tagX };
              };

              // The highlighted deploy's tag is always drawn, so the others
              // give way to it rather than the other way round.
              const held = active !== null ? place(active) : null;
              const clashes = (tagX: number, tagW: number) =>
                held !== null &&
                held.tagW > 0 &&
                tagX < held.tagX + held.tagW + 4 * px &&
                held.tagX < tagX + tagW + 4 * px;

              let lastTagRight = -Infinity;
              deploys.forEach((_d, i) => {
                if (i === active) return;
                const { d, x, tagW, tagX } = place(i);
                if (x < left - 1 || x > left + width + 1) return;
                // Everything but the highlighted deploy steps back.
                ctx.globalAlpha = held ? 0.3 : 1;
                const color = d.failed ? colors.error : colors.deploy;
                ctx.strokeStyle = color;
                ctx.lineWidth = px;
                ctx.setLineDash([3 * px, 3 * px]);
                ctx.beginPath();
                ctx.moveTo(x, top);
                ctx.lineTo(x, top + height);
                ctx.stroke();

                // Tags never overlap: one too close to the last drawn is
                // skipped, and the readout still names every deploy.
                if (!tagW || tagX < lastTagRight + 4 * px || clashes(tagX, tagW)) return;
                ctx.setLineDash([]);
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.roundRect(tagX, tagY, tagW, tagH, 3 * px);
                ctx.fill();
                ctx.fillStyle = colors.surface;
                ctx.fillText(d.version, tagX + padX, tagY + tagH / 2);
                lastTagRight = tagX + tagW;
              });

              if (held) {
                const { d, x, tagW, tagX } = held;
                const color = d.failed ? colors.error : colors.deploy;
                ctx.globalAlpha = 1;
                ctx.strokeStyle = color;
                ctx.lineWidth = 2 * px;
                ctx.setLineDash([]);
                ctx.beginPath();
                ctx.moveTo(x, top);
                ctx.lineTo(x, top + height);
                ctx.stroke();
                if (tagW) {
                  ctx.fillStyle = color;
                  ctx.beginPath();
                  ctx.roundRect(tagX, tagY, tagW, tagH, 3 * px);
                  ctx.fill();
                  ctx.fillStyle = colors.surface;
                  ctx.fillText(d.version, tagX + padX, tagY + tagH / 2);
                }
              }
              ctx.restore();
            },
          ],
          setCursor: [
            (u) => {
              const index = u.cursor.idx;
              const pointer = u.cursor.left;
              if (index == null || pointer == null || pointer < 0) {
                tip!.hidden = true;
                if (active !== null) {
                  active = null;
                  u.redraw(false);
                }
                return;
              }
              const near = nearestDeploy(u, pointer);
              if (near !== active) {
                active = near;
                u.redraw(false);
                if (near !== null && !deployFocused) {
                  deployFocused = true;
                  deployFocusRef.current?.();
                }
              }
              renderTip(index);
              if (!hovered) {
                hovered = true;
                hoverRef.current?.(lastInput);
              }
              // Anchored to the snapped bucket, not the pointer, and flipped to
              // the left of the hairline when it would run off the right edge.
              const overRect = u.over.getBoundingClientRect();
              const hostRect = host!.getBoundingClientRect();
              const snapped = overRect.left - hostRect.left + u.valToPos(xs[index], 'x');
              const tipWidth = tip!.offsetWidth;
              let x = snapped + 12;
              if (x + tipWidth > hostRect.width) x = snapped - tipWidth - 12;
              tip!.style.transform = `translate(${Math.max(0, x)}px, 0)`;
            },
          ],
        },
      };

      plot = new uPlot(opts, data, target!);
    }

    function row(value: string, name: string, tone: 'ink' | 'error'): HTMLElement {
      const line = document.createElement('div');
      line.className = 'rc__row';
      const key = document.createElement('span');
      key.className = 'rc__key';
      key.dataset.tone = tone;
      const num = document.createElement('strong');
      num.className = 'rc__value';
      num.textContent = value;
      const what = document.createElement('span');
      what.className = 'rc__name';
      what.textContent = name;
      line.append(key, num, what);
      return line;
    }

    function deployNote(d: (typeof deploys)[number], isActive: boolean): HTMLElement {
      const note = document.createElement('div');
      note.className = 'rc__deploy';
      note.dataset.failed = d.failed ? 'yes' : 'no';
      note.dataset.active = isActive ? 'yes' : 'no';
      const time = timeFmt.format(new Date(d.sec * 1000));
      note.textContent = d.failed ? words.deployFailed(d.version, time) : words.deploy(d.version, time);
      return note;
    }

    function renderTip(index: number) {
      const t = tip!;
      t.replaceChildren();
      // The highlighted deploy leads the readout: it is what the pointer is on.
      if (active !== null) t.append(deployNote(deploys[active], true));
      const head = document.createElement('div');
      head.className = 'rc__when';
      head.textContent = index === lastIndex ? `${bucketTitle(index)} (${words.soFar})` : bucketTitle(index);
      t.append(head, row(count(reqs[index] ?? 0), words.requests, 'ink'));
      if (showErrors) t.append(row(count(errs[index] ?? 0), words.errors, 'error'));
      const from = xs[index];
      const to = from + stepSec;
      deploys.forEach((d, i) => {
        if (i !== active && d.sec >= from && d.sec < to) t.append(deployNote(d, false));
      });
      t.hidden = false;
    }

    build();

    // Phones have no hover, so a finger HOLDS the chart and drags. A quick
    // swipe still scrolls the page: only a touch that stays put for HOLD_MS
    // takes over, and from then on the page does not scroll under it.
    const HOLD_MS = 300;
    const SLOP_PX = 10;
    let holdTimer: number | undefined;
    let startX = 0;
    let startY = 0;

    function cursorAt(touch: Touch) {
      if (!plot) return;
      const rect = plot.over.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, touch.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, touch.clientY - rect.top));
      plot.setCursor({ left: x, top: y });
    }

    function onTouchStart(e: TouchEvent) {
      if (e.touches.length !== 1) return;
      lastInput = 'touch';
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(() => {
        touching = true;
        host!.dataset.holding = 'yes';
        cursorAt(touch);
      }, HOLD_MS);
    }

    function onTouchMove(e: TouchEvent) {
      const touch = e.touches[0];
      if (!touch) return;
      if (touching) {
        e.preventDefault();
        cursorAt(touch);
        return;
      }
      // Moved before the hold landed: that was a scroll, so let it be one.
      if (Math.hypot(touch.clientX - startX, touch.clientY - startY) > SLOP_PX) {
        window.clearTimeout(holdTimer);
      }
    }

    function onTouchEnd() {
      window.clearTimeout(holdTimer);
      // The readout stays where the finger lifted, so it can be read. The
      // next touch anywhere on the chart starts over.
      touching = false;
      delete host!.dataset.holding;
    }

    function onMouseMove() {
      lastInput = 'mouse';
    }

    target.addEventListener('touchstart', onTouchStart, { passive: true });
    target.addEventListener('touchmove', onTouchMove, { passive: false });
    target.addEventListener('touchend', onTouchEnd);
    target.addEventListener('touchcancel', onTouchEnd);
    target.addEventListener('mousemove', onMouseMove, { passive: true });

    const resize = new ResizeObserver(() => {
      if (plot) plot.setSize({ width: Math.max(200, target.clientWidth), height: target.clientHeight || 200 });
    });
    resize.observe(target);

    // The theme flips by attribute, and a canvas holds resolved colours, so a
    // flip is a rebuild.
    const themes = new MutationObserver(build);
    themes.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-direction', 'data-theme'],
    });

    return () => {
      window.clearTimeout(holdTimer);
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchmove', onTouchMove);
      target.removeEventListener('touchend', onTouchEnd);
      target.removeEventListener('touchcancel', onTouchEnd);
      target.removeEventListener('mousemove', onMouseMove);
      resize.disconnect();
      themes.disconnect();
      plot?.destroy();
      plot = null;
    };
  }, [dataKey, wordsKey, bucket, showErrors, locale]);

  return (
    <div className="rc" ref={hostRef}>
      <div className="rc__plot" ref={plotRef} role="img" aria-label={label} />
      <div className="rc__tip" ref={tipRef} hidden aria-hidden="true" />
    </div>
  );
}
